import * as React from 'react'
import cn from 'classnames'
import { CaretDown, Check, X } from '@phosphor-icons/react'
import { Editor } from 'components/atoms/editor'
import { ConfirmationDialog } from 'src/design-system'
import { OffsetDirection, RecipientGroup, NotificationDraft } from './types'
import { RECIPIENT_GROUP_OPTIONS, blankNotificationDraft } from './helpers'
import {
  fieldLabelClass,
  baseInputClass,
  inputClass,
  FieldError,
  TIME_OPTIONS,
  TIMEZONE_OPTIONS,
  areEmailsValid,
  hasDuplicateEmails,
  parseEmailList,
  htmlHasContent,
  OFFSET_DAYS_MAX,
  clampOffsetDays,
  NAME_MAX_LENGTH,
  SUBJECT_MAX_LENGTH,
  subjectError,
  notificationNameError,
} from './form-helpers'
import { StepDots } from './step-dots'

// Shared 2-step "New / Edit email notification" modal (copied + generalised from
// the check-in cycle wizard). Operates purely on a NotificationDraft — it never
// touches the API. The parent appends/updates the returned draft in local state;
// nothing persists until the page's "Save Settings".

type StepKey = 'settings' | 'content'

const STEP_LABELS: Record<StepKey, string> = {
  settings: 'Settings',
  content: 'Email Template',
}

const TIMING_OPTIONS: Array<{ value: OffsetDirection; label: string }> = [
  { value: 'on_event', label: 'On event' },
  { value: 'before_event', label: 'Before event' },
  { value: 'after_event', label: 'After event' },
]

// "Send to" is a two-column checkbox grid filled top-to-bottom (column-major):
// the first half of the options goes down the left column, the rest down the right.
const RECIPIENT_COLUMN_SIZE = Math.ceil(RECIPIENT_GROUP_OPTIONS.length / 2)

type NotificationModalProps = {
  // The reminder being edited, or null to create a new one.
  notification: NotificationDraft | null
  onSave: (draft: NotificationDraft) => void
  onClose: () => void
  // Names of the OTHER reminders in the list, used to enforce a unique name.
  existingNames?: string[]
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  notification,
  onSave,
  onClose,
  existingNames = [],
}) => {
  const isEditing = notification !== null
  const initial = notification ?? blankNotificationDraft()

  const [name, setName] = React.useState(initial.name)
  const [offsetDirection, setOffsetDirection] = React.useState<OffsetDirection>(
    initial.offsetDirection,
  )
  const [offsetDays, setOffsetDays] = React.useState(initial.offsetDays)
  const [sendTime, setSendTime] = React.useState(initial.sendTime ?? '9 AM')
  const [sendTimezone, setSendTimezone] = React.useState(
    initial.sendTimezone ?? 'America/New_York',
  )
  const [recipientGroups, setRecipientGroups] = React.useState<
    RecipientGroup[]
  >(initial.recipientGroups)
  const [additionalEmails, setAdditionalEmails] = React.useState(
    initial.additionalEmails.join(', '),
  )
  const [emailSubject, setEmailSubject] = React.useState(initial.emailSubject)
  const [emailBody, setEmailBody] = React.useState(initial.emailBody)

  const [step, setStep] = React.useState<StepKey>('settings')
  const [submitAttempted, setSubmitAttempted] = React.useState(false)
  const [confirmingDiscard, setConfirmingDiscard] = React.useState(false)

  // Cancel / close / Escape always ask before discarding, per the ticket: the
  // confirmation warns that any unsaved draft changes will be lost.
  const requestDiscard = () => setConfirmingDiscard(true)

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // The nested ConfirmationDialog (Radix AlertDialog) preventDefaults the
      // Escape it consumes to close itself — bail so this listener doesn't
      // immediately reopen the discard-confirmation right after it's dismissed.
      if (event.defaultPrevented) return
      if (event.key === 'Escape') requestDiscard()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const usesOffset = offsetDirection !== 'on_event'
  const showAdditionalEmails = recipientGroups.includes('additional_recipients')

  // Required + must contain a letter + must not clash with another reminder;
  // see notificationNameError, which mirrors the server-side rules.
  const nameError = notificationNameError(name, submitAttempted, existingNames)

  // Recipient email(s): required + well-formed only when the "additional
  // recipients" group is selected (that's when the field is shown). Show the
  // error once the user has typed something invalid or attempted to continue.
  const emailsValid = areEmailsValid(additionalEmails)
  const emailErrorMessage = hasDuplicateEmails(additionalEmails)
    ? 'Duplicate email addresses detected. Each address should be unique.'
    : 'Please enter a valid email address'
  const emailError =
    showAdditionalEmails &&
    !emailsValid &&
    (submitAttempted || additionalEmails.trim() !== '')
      ? emailErrorMessage
      : null

  // Screen 2 (Email Template): subject and body are both required and may not
  // be whitespace-only. The subject additionally has a max length and must
  // contain a letter (see subjectError). `emailBody` is editor HTML, so use the
  // tag-aware check. Validity is derived from subjectError itself (passing
  // submitAttempted: true so a blank subject counts) rather than re-listing its
  // rules here, so the Save gate and the inline message can't drift apart.
  const bodyValid = htmlHasContent(emailBody)
  const contentValid = subjectError(emailSubject, true) === null && bodyValid
  const subjectErrorMessage = subjectError(emailSubject, submitAttempted)
  const bodyError = submitAttempted && !bodyValid ? 'Body is required' : null

  // At least one "Send to" recipient group must be selected.
  const recipientError =
    submitAttempted && recipientGroups.length === 0
      ? 'Select at least one recipient'
      : null

  // Same derivation as contentValid: reuse the error helper so Continue is gated
  // on exactly the rules the field reports.
  const settingsValid =
    notificationNameError(name, true, existingNames) === null &&
    recipientGroups.length > 0 &&
    (!showAdditionalEmails || emailsValid)

  const toggleRecipientGroup = (group: RecipientGroup) => {
    setRecipientGroups((current) =>
      current.includes(group)
        ? current.filter((value) => value !== group)
        : [...current, group],
    )
    // Unchecking "Additional Recipients" hides the address field; clear it too so
    // a stale value can't linger in state (and later be persisted on save) after
    // the group is deselected. `showAdditionalEmails` is the pre-toggle value, so
    // it's true exactly when this toggle is turning the group off.
    if (group === 'additional_recipients' && showAdditionalEmails) {
      setAdditionalEmails('')
    }
  }

  const handleContinue = () => {
    setSubmitAttempted(true)
    if (!settingsValid) return
    setSubmitAttempted(false)
    setStep('content')
  }

  const handleSave = () => {
    setSubmitAttempted(true)
    if (!contentValid) return

    const emails = parseEmailList(additionalEmails)

    onSave({
      ...initial,
      name: name.trim(),
      offsetDirection,
      offsetDays: usesOffset ? offsetDays : 0,
      sendTime: usesOffset ? sendTime : null,
      sendTimezone: usesOffset ? sendTimezone : null,
      recipientGroups,
      // Only persist addresses when the group is actually selected — never save a
      // value left over from a since-deselected "Additional Recipients".
      additionalEmails: showAdditionalEmails ? emails : [],
      emailSubject: emailSubject.trim(),
      emailBody,
    })
  }

  const stepNumber = step === 'settings' ? 1 : 2

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(29,38,74,0.22)] px-4 py-4">
      {/* Figma frame: 628×689, 20px padding, 16px gap. Fixed 689px height sized to
          the TALLEST Step-1 state (Timing offset + "Additional Recipients" email
          field shown) so toggling that checkbox never changes the modal height, and
          it's identical across every step. Shorter states leave empty space above
          the footer; content taller than this (e.g. the Step-2 editor) scrolls in
          the body. Capped to the viewport (max-h-full) as a small-screen fallback.
          align-items:flex-end from Figma is intentionally NOT applied — on the web
          it would collapse the full-width header/fields to content width; the
          footer is right-aligned on its own row instead. */}
      <div className="flex h-[689px] max-h-full w-full max-w-[628px] flex-col gap-4 rounded-lg bg-white p-5 shadow-[0_18px_44px_rgba(29,38,74,0.18)] outline outline-1 outline-offset-[-1px] outline-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-talent text-2xl font-medium leading-7 text-[#1D264A]">
              {isEditing ? 'Edit email notification' : 'New email notification'}
            </h2>
            <p className="font-talent text-base font-normal leading-5 text-[#9aa1ba]">
              Step {stepNumber} of 2 &middot; {STEP_LABELS[step]}
            </p>
          </div>

          <button
            type="button"
            onClick={requestDiscard}
            aria-label="Close"
            className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-[#EFF1F8] text-[#5E6580] hover:bg-[#E3E8F1]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="h-px w-full bg-[#E7EAF4]" />

        {/* overflow-y-auto forces overflow-x to clip too, which would shave the
            2px focus ring off inputs on the left/right. -mx-5 px-5 lets the
            scroll area span into the card's p-5 padding so the ring has room,
            while the px-5 keeps field content aligned with the header/footer. */}
        <div className="-mx-5 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5">
          {step === 'settings' ? (
            <>
              <div>
                <label htmlFor="notification-name" className={fieldLabelClass}>
                  Notification Name
                </label>
                <input
                  id="notification-name"
                  type="text"
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Title placeholder"
                  className={inputClass(Boolean(nameError))}
                />
                <FieldError message={nameError} />
              </div>

              <div>
                <p className={fieldLabelClass}>Timing</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {TIMING_OPTIONS.map((option) => {
                      const isActive = offsetDirection === option.value

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setOffsetDirection(option.value)}
                          aria-pressed={isActive}
                          className={cn(
                            'flex h-9 items-center rounded-full px-3 font-talent text-base font-normal leading-5 transition-colors',
                            isActive
                              ? 'bg-[#2E9E5D] text-white'
                              : 'border !border-[#D7DBE8] bg-white text-[#5E6580] hover:!border-[#2E9E5D]',
                          )}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>

                  {usesOffset && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={OFFSET_DAYS_MAX}
                        value={offsetDays}
                        onChange={(event) =>
                          setOffsetDays(clampOffsetDays(event.target.value))
                        }
                        className="h-11 w-20 rounded-sm border-0 bg-[#EFF1F8] px-4 font-talent text-base text-[#1D264A] focus:outline-none focus:ring-2 focus:ring-[#2E9E5D]"
                      />
                      <span className="font-talent text-base text-[#5E6580]">
                        {offsetDirection === 'before_event'
                          ? 'days before'
                          : 'days after'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {usesOffset && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="send-time" className={fieldLabelClass}>
                      Start Time
                    </label>
                    <div className="relative">
                      <select
                        id="send-time"
                        value={sendTime}
                        onChange={(event) => setSendTime(event.target.value)}
                        className={cn(
                          baseInputClass,
                          'appearance-none pr-11 font-talent focus:ring-[#2E9E5D]',
                        )}
                      >
                        {TIME_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <CaretDown
                        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
                        size={18}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="send-timezone" className={fieldLabelClass}>
                      Time Zone
                    </label>
                    <div className="relative">
                      <select
                        id="send-timezone"
                        value={sendTimezone}
                        onChange={(event) =>
                          setSendTimezone(event.target.value)
                        }
                        className={cn(
                          baseInputClass,
                          'appearance-none pr-11 font-talent focus:ring-[#2E9E5D]',
                        )}
                      >
                        {TIMEZONE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <CaretDown
                        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
                        size={18}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <p className={fieldLabelClass}>Send to</p>
                <div className="rounded-lg bg-[#F7F8FB] p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                    {(
                      [
                        [
                          'left',
                          RECIPIENT_GROUP_OPTIONS.slice(
                            0,
                            RECIPIENT_COLUMN_SIZE,
                          ),
                        ],
                        [
                          'right',
                          RECIPIENT_GROUP_OPTIONS.slice(RECIPIENT_COLUMN_SIZE),
                        ],
                      ] as const
                    ).map(([columnKey, column]) => (
                      <div
                        key={columnKey}
                        className="flex flex-1 flex-col gap-3"
                      >
                        {column.map((option) => {
                          const checked = recipientGroups.includes(option.value)

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => toggleRecipientGroup(option.value)}
                              aria-pressed={checked}
                              className="flex items-center gap-3 text-left"
                            >
                              <span
                                className={cn(
                                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-[1.5px] border-solid transition-colors',
                                  checked
                                    ? 'border-[#1D264A] bg-[#1D264A]'
                                    : 'border-[#C7CCDD] bg-white',
                                )}
                              >
                                {checked && (
                                  <Check
                                    size={12}
                                    weight="bold"
                                    className="text-white"
                                  />
                                )}
                              </span>
                              <span className="font-talent text-base leading-5 text-[#1D264A]">
                                {option.label}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                <FieldError message={recipientError} />
              </div>

              {showAdditionalEmails && (
                <div>
                  <label
                    htmlFor="additional-emails"
                    className={fieldLabelClass}
                  >
                    Enter Email Addresses
                  </label>
                  <input
                    id="additional-emails"
                    type="text"
                    value={additionalEmails}
                    onChange={(event) =>
                      setAdditionalEmails(event.target.value)
                    }
                    placeholder="example1@careerminds.com, example2@careerminds.com"
                    className={inputClass(Boolean(emailError))}
                  />
                  {emailError ? (
                    <FieldError message={emailError} />
                  ) : (
                    <p className="mt-1.5 text-[13px] text-[#9aa1ba]">
                      Separate multiple addresses with a comma
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label htmlFor="email-subject" className={fieldLabelClass}>
                  Subject
                </label>
                <input
                  id="email-subject"
                  type="text"
                  value={emailSubject}
                  maxLength={SUBJECT_MAX_LENGTH}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  className={inputClass(Boolean(subjectErrorMessage))}
                />
                <FieldError message={subjectErrorMessage} />
              </div>

              {/* Body fills the modal's remaining height; the editor scrolls
                  internally (max-height 20rem) so a long template never grows the
                  modal or shows a body scrollbar. */}
              <div className="flex min-h-0 flex-1 flex-col">
                <p className={fieldLabelClass}>Body</p>
                <div className="flex min-h-0 flex-1 flex-col rounded-lg bg-[#EFF1F8] p-2">
                  <Editor
                    toolbar="static"
                    initialContent={emailBody}
                    onChange={setEmailBody}
                    invalid={Boolean(bodyError)}
                    className="min-h-0 flex-1"
                  />
                </div>
                <FieldError message={bodyError} />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          <StepDots totalSteps={2} currentStep={stepNumber} />

          <div className="flex items-center gap-3">
            {step === 'content' && (
              <button
                type="button"
                onClick={requestDiscard}
                className="inline-flex items-center justify-center px-3.5 py-2 font-talent text-base font-medium leading-5 text-[#5E6580] hover:text-[#1D264A]"
              >
                Cancel
              </button>
            )}
            {step === 'content' && (
              <button
                type="button"
                onClick={() => setStep('settings')}
                className="inline-flex items-center justify-center rounded-sm border border-[#D7DBE8] bg-white px-3.5 py-2 text-base font-medium leading-5 text-[#1D264A] hover:bg-[#F7F8FB]"
              >
                Back
              </button>
            )}

            {step === 'settings' ? (
              <button
                type="button"
                onClick={handleContinue}
                aria-disabled={!settingsValid}
                className={cn(
                  'inline-flex items-center justify-center rounded-sm px-3.5 py-2 font-talent text-base font-medium leading-5 transition-colors',
                  settingsValid
                    ? 'bg-[#2E9E5D] text-white hover:bg-[#268650]'
                    : 'bg-gray-200 text-gray-500',
                )}
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                aria-disabled={!contentValid}
                className={cn(
                  'inline-flex items-center justify-center rounded-sm px-3.5 py-2 font-talent text-base font-medium leading-5 transition-colors',
                  contentValid
                    ? 'bg-[#2E9E5D] text-white hover:bg-[#268650]'
                    : 'bg-gray-200 text-gray-500',
                )}
              >
                {isEditing ? 'Save changes' : 'Create'}
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog.Root
        open={confirmingDiscard}
        onOpenChange={setConfirmingDiscard}
        title="Are you sure you want to discard your changes?"
        body="Any unsaved draft changes to the reminder will be permanently removed."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmColourVariant="danger"
        onConfirm={() => {
          setConfirmingDiscard(false)
          onClose()
        }}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </div>
  )
}
